import { ChangeEvent, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CustomerInvoiceProfile,
  CustomerInvoiceProfileFormErrors,
  CustomerInvoiceProfileFormValues,
  UpdateCustomerInvoiceProfileDto
} from '@/types/CustomerInvoiceProfile.interface';
import {
  buildProfileUpdateDto,
  toProfileFormValues,
  validateProfileForm
} from '@/utils/customerInvoiceProfile';

interface CustomerInvoiceProfileDialogProps {
  open: boolean;
  profile: CustomerInvoiceProfile | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (dto: UpdateCustomerInvoiceProfileDto) => void | Promise<void>;
}

const EMPTY_VALUES: CustomerInvoiceProfileFormValues = {
  name: '',
  taxOffice: '',
  address: '',
  buildingNumber: '',
  district: '',
  city: '',
  postalCode: '',
  email: '',
  phone: ''
};

/**
 * Edit dialog of a registered e-Belge buyer. The tax id is shown read only
 * (it is fixed once the buyer is registered at the POS). A VKN buyer (entityType
 * COMPANY) must keep a tax office.
 */
const CustomerInvoiceProfileDialog = ({
  open,
  profile,
  saving = false,
  onClose,
  onSave
}: CustomerInvoiceProfileDialogProps) => {
  const { t } = useTranslation();
  const [values, setValues] =
    useState<CustomerInvoiceProfileFormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<CustomerInvoiceProfileFormErrors>({});

  useEffect(() => {
    if (open && profile) {
      setValues(toProfileFormValues(profile));
      setErrors({});
    }
  }, [open, profile]);

  if (!profile) return null;

  const isCompany = profile.entityType === 'COMPANY';

  const setField =
    (field: keyof CustomerInvoiceProfileFormValues) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setValues((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const errorText = (field: keyof CustomerInvoiceProfileFormValues) =>
    errors[field] ? t(errors[field] as string) : undefined;

  const handleSubmit = () => {
    const nextErrors = validateProfileForm(values, profile.entityType);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const dto = buildProfileUpdateDto(values, profile);
    if (Object.keys(dto).length === 0) {
      onClose();
      return;
    }
    void onSave(dto);
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t('customerInvoiceProfiles.dialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label={t(
                `customerInvoiceProfiles.field.taxId.${profile.entityType}`
              )}
              value={profile.taxId}
              helperText={t('customerInvoiceProfiles.field.taxId.readOnly')}
              disabled
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('customerInvoiceProfiles.field.name')}
              value={values.name}
              onChange={setField('name')}
              error={Boolean(errors.name)}
              helperText={errorText('name')}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('customerInvoiceProfiles.field.taxOffice')}
              value={values.taxOffice}
              onChange={setField('taxOffice')}
              error={Boolean(errors.taxOffice)}
              helperText={errorText('taxOffice')}
              required={isCompany}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('customerInvoiceProfiles.field.email')}
              value={values.email}
              onChange={setField('email')}
              error={Boolean(errors.email)}
              helperText={errorText('email')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('customerInvoiceProfiles.field.phone')}
              value={values.phone}
              onChange={setField('phone')}
              inputProps={{ inputMode: 'tel' }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              label={t('customerInvoiceProfiles.field.address')}
              value={values.address}
              onChange={setField('address')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label={t('customerInvoiceProfiles.field.buildingNumber')}
              value={values.buildingNumber}
              onChange={setField('buildingNumber')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('customerInvoiceProfiles.field.district')}
              value={values.district}
              onChange={setField('district')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('customerInvoiceProfiles.field.city')}
              value={values.city}
              onChange={setField('city')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('customerInvoiceProfiles.field.postalCode')}
              value={values.postalCode}
              onChange={setField('postalCode')}
              inputProps={{ inputMode: 'numeric' }}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerInvoiceProfileDialog;
